import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { readLastKnown, writeLastKnown } from './lastKnown';
import { CAP, type ChatDeliveryMode, type OnLogicClaims } from '@onlogic/shared';

/**
 * Read the tri-state `config/features.chatDelivery` value. The boolean flag
 * derived from it (see FeatureFlags.chatDelivery) collapses 'off' and 'dryRun'
 * together, which is right for gating UI but loses the distinction the rollout
 * is actually staged on — so the admin control reads the raw mode through here.
 * Anything unrecognised, including a missing document, is 'off': the same
 * default the server applies (readChatConfig in scheduledPushDelivery).
 */
export function parseChatDeliveryMode(raw: unknown): ChatDeliveryMode {
  if (raw === 'on' || raw === true) return 'on';
  if (raw === 'dryRun') return 'dryRun';
  return 'off';
}

export interface FeatureFlags {
  surveys: boolean;
  events: boolean;
  cars: boolean;
  // Already-LIVE modules (unlike the placeholders below, which launched dark).
  // These flags exist so an admin can take a module down for maintenance, so
  // they DEFAULT TRUE — a missing config/features field must keep the live
  // module visible, not hide it. Turning one off hides its nav entry, Home
  // tile, and routes for everyone except systemAdmin and the module's owner
  // capability (facilitiesAdmin / finance — see isFeatureEnabled).
  purchaseRequests: boolean;
  facilities: boolean;
  // Unbuilt placeholder module — gated off for regular employees so the
  // "coming soon" stub isn't reachable from the nav. Admins with the mapped
  // capability (see isFeatureEnabled) can still preview it.
  aircraft: boolean;
  // Whether the Google Chat delivery channel is LIVE (Chat plan, Part 1).
  // Derived from the tri-state `config/features.chatDelivery` mode
  // ('off' | 'dryRun' | 'on'): true only when 'on'. Gates the Settings
  // "Notification Delivery" toggle so it stays hidden until Chat actually
  // delivers; systemAdmins can preview via the isFeatureEnabled override.
  chatDelivery: boolean;
  // Whether interactive in-Chat actions are LIVE (IMP-056) — Approve/Reject a
  // purchase request and Acknowledge/Reply/Resolve/Assign a ticket from a Chat
  // card. Plain boolean. Gates BOTH the notification triggers (whether buttons
  // are attached) and the Chat endpoint (whether a click acts); the server
  // independently enforces it. Default off until the Chat App's interactive
  // features are provisioned (see docs/OPS-CHECKLIST.md).
  chatActions: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  surveys: false,
  events: false,
  cars: false,
  // Live modules — default TRUE so an absent field never hides them (see the
  // interface comment). Only an explicit `false` in config/features hides them.
  purchaseRequests: true,
  facilities: true,
  aircraft: false,
  chatDelivery: false,
  chatActions: false,
};

/**
 * The config/features fields the client reads. Only these are kept as the
 * last-known copy (lib/lastKnown.ts) — the document is readable by every
 * signed-in account and says nothing beyond which modules are switched on.
 */
const FLAG_FIELDS = [
  'surveys',
  'events',
  'cars',
  'purchaseRequests',
  'facilities',
  'aircraft',
  'chatDelivery',
  'chatActions',
] as const;

const LAST_KNOWN_KEY = 'featureFlags';

/** Keep only the known flag fields of a config/features document (or a stored copy). */
export function pickFlagFields(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of FLAG_FIELDS) {
    if (key in source) out[key] = source[key];
  }
  return out;
}

/**
 * Parse config/features into the UI flags plus the raw Chat rollout mode. The
 * live snapshot and the last-known copy both go through here, so they cannot
 * disagree about defaults. A missing document is an empty object.
 */
export function flagsFromDoc(data: Record<string, unknown>): {
  flags: FeatureFlags;
  chatDeliveryMode: ChatDeliveryMode;
} {
  // Absent or non-boolean field → DEFAULT_FLAGS, which keeps the live modules
  // (purchaseRequests, facilities) visible and the rest hidden.
  const bool = (key: keyof FeatureFlags): boolean =>
    typeof data[key] === 'boolean' ? (data[key] as boolean) : DEFAULT_FLAGS[key];
  return {
    chatDeliveryMode: parseChatDeliveryMode(data.chatDelivery),
    flags: {
      surveys: bool('surveys'),
      events: bool('events'),
      cars: bool('cars'),
      purchaseRequests: bool('purchaseRequests'),
      facilities: bool('facilities'),
      aircraft: bool('aircraft'),
      // config/features.chatDelivery is the tri-state ChatDeliveryMode
      // ('off' | 'dryRun' | 'on'); the toggle is meaningful only once Chat
      // is live, so the flag is true only for 'on' (legacy boolean `true`
      // also accepted).
      chatDelivery: data.chatDelivery === 'on' || data.chatDelivery === true,
      chatActions: bool('chatActions'),
    },
  };
}

export function useFeatureFlags() {
  // Start from the last-known flags when this device has them (IMP-062): the
  // Home page now renders before Firestore can answer, and without them every
  // launch drew the default tile set and then reshuffled it. The live snapshot
  // below replaces them moments later. UX only — FeatureGate hides pages, it
  // authorizes nothing; the server enforces every module's rules itself.
  const [initial] = useState(() => {
    const cached = readLastKnown(LAST_KNOWN_KEY, pickFlagFields);
    return cached ? flagsFromDoc(cached) : null;
  });
  const [flags, setFlags] = useState<FeatureFlags>(() => initial?.flags ?? DEFAULT_FLAGS);
  // The raw rollout mode behind flags.chatDelivery, for the admin control that
  // sets it. Kept beside `flags` rather than in it so FeatureFlags stays a flat
  // boolean map (isFeatureEnabled indexes it by key).
  const [chatDeliveryMode, setChatDeliveryMode] = useState<ChatDeliveryMode>(
    () => initial?.chatDeliveryMode ?? 'off',
  );
  const [loading, setLoading] = useState(() => !initial);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'config', 'features'),
      (snapshot) => {
        // No config/features document at all — every flag falls to its
        // default and Chat delivery is off, matching the server.
        const data = (snapshot.exists() && pickFlagFields(snapshot.data())) || {};
        writeLastKnown(LAST_KNOWN_KEY, data);
        const next = flagsFromDoc(data);
        setFlags(next.flags);
        setChatDeliveryMode(next.chatDeliveryMode);
        setLoading(false);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching feature flags:', error);
        setFlags(DEFAULT_FLAGS);
        setChatDeliveryMode('off');
        setLoading(false);
      }
    );

    return unsub;
  }, []);

  return { flags, chatDeliveryMode, loading };
}

export function isFeatureEnabled(
  feature: keyof FeatureFlags,
  flags: FeatureFlags,
  claims: OnLogicClaims | null
): boolean {
  if (!flags) return false;
  // If the flag is explicitly enabled, anyone can access it
  if (flags[feature]) return true;

  // Otherwise, it's disabled. Only users with administrative capabilities can access/test it
  if (!claims) return false;

  // systemAdmin previews everything while a flag is off.
  if (claims.capabilities.includes(CAP.systemAdmin)) return true;

  // Feature-specific capabilities: each module's admin can preview their
  // module while its flag is off (and, for aircraft, keep reaching the
  // Manage tab + /aircraft/settings if the flag is ever turned back off).
  if (feature === 'surveys' && claims.capabilities.includes(CAP.surveyAdmin)) return true;
  if (feature === 'events' && claims.capabilities.includes(CAP.eventsAdmin)) return true;
  if (feature === 'cars' && claims.capabilities.includes(CAP.fleetAdmin)) return true;
  if (feature === 'aircraft' && claims.capabilities.includes(CAP.flightAdmin)) return true;
  // Live modules default on; these branches only matter if an admin turns the
  // flag off — the module owner keeps preview access (matches AdminSystemPage's
  // module-settings owners: Facilities → facilitiesAdmin, PR → finance).
  if (feature === 'facilities' && claims.capabilities.includes(CAP.facilitiesAdmin)) return true;
  if (feature === 'purchaseRequests' && claims.capabilities.includes(CAP.finance)) return true;

  return false;
}
